// Display rules for the review page, kept out of the components so they can be
// tested as plain functions. Every input is a slot or item from
// GET /photo-pipeline/review/pending.

export const REJECTION_REASONS = ['out_of_focus', 'wrong_angle', 'lighting', 'dirty_or_damaged', 'cropped', 'other'];

export function slotState(slot) {
    if (slot.status === 'rejected') return 'rejected';
    if (slot.status === 'approved') return 'approved';
    // markError sets `error` without touching status, so a failed RE-edit leaves
    // a pending_approval shot with an edit still worth approving.
    if (slot.status === 'pending_approval') return 'ready';
    // `editing` and `queued` come from the server: process-now keeps a shot
    // `uploaded` while it edits it, so status alone can't tell a shot being
    // edited (or next in the run) from one waiting for the button.
    if (slot.editing) return 'editing';
    if (slot.hasError) return 'failed';
    if (slot.status === 'batching') return 'batching';
    if (slot.queued) return 'queued';
    return 'waiting';
}

export function pieceBadges(item) {
    const counts = { approved: 0, total: item.slots.length, ready: 0, waiting: 0, queued: 0, editing: 0, batching: 0, failed: 0, rejected: 0 };
    item.slots.forEach(slot => { counts[slotState(slot)] += 1; });
    return counts;
}

// While the server is editing or has queued a shot, the page re-reads the
// queue so each edit shows as it lands. A batch takes hours: not polled.
export function queueIsBusy(items) {
    return (items || []).some(item => item.slots.some(slot => slot.editing || slot.queued));
}

export function readyPhotoIds(item) {
    return item.slots.filter(slot => slotState(slot) === 'ready').map(slot => slot.photoId);
}

// `inFlight` maps a photo to the `attempts` its re-edit started from. The shot
// is regenerating until the queue shows a newer version, which can happen
// before the request itself answers (the queue is polled meanwhile).
export function isRegenerating(inFlight, slot) {
    return inFlight.has(slot.photoId) && slot.attempts <= inFlight.get(slot.photoId)
}

// `size` 'preview' is the server's ~1000px version: what the page shows until
// the reviewer zooms in. Omitted, it is the full-size image.
export function imageUrl(photoId, variant, version, size) {
    const base = `${process.env.REACT_APP_API_BASE_URL}/dashboard/photo-pipeline/review/image/${photoId}/${variant}?v=${version}`
    return size ? `${base}&size=${size}` : base
}

export function thumbnailFor(item) {
    const edited = item.slots.find(slot => ['ready', 'approved'].includes(slotState(slot)));
    if (edited) return { photoId: edited.photoId, variant: 'edited', version: edited.attempts };
    const first = item.slots[0];
    return first ? { photoId: first.photoId, variant: 'raw', version: first.rawVersion } : null;
}

// What the server allows in a Content-Disposition filename (its safeFilenamePart).
const safeFilenamePart = value => String(value).replace(/[^A-Za-z0-9._-]/g, '_');

// One version's full-size PNG (shadow included): the current one by default,
// or a listed version by its attemptNo. Named <sku>_<slot>_a<n>.png like the
// server's Content-Disposition, which the page can't read cross-origin; the
// current version is named after the version it shows.
export function downloadTarget(sku, slot, attemptNo) {
    const base = `${process.env.REACT_APP_API_BASE_URL}/dashboard/photo-pipeline/review/${slot.photoId}/download`;
    const shown = attemptNo ?? (slot.versions || []).find(version => version.current)?.attemptNo ?? slot.attempts;
    return {
        url: attemptNo === undefined ? base : `${base}?attempt=${attemptNo}`,
        filename: `${safeFilenamePart(sku)}_${safeFilenamePart(slot.slot)}_a${shown}.png`,
    };
}

export const REPLACE_MAX_BYTES = 30 * 1024 * 1024;
const replaceError = (name, values = {}) => ({ key: `photoReview.replace.errors.${name}`, values });

// What the server would refuse anyway, caught before a 30 MB upload. The
// server judges PNG by the bytes; this only reads the type and the name.
export function checkReplaceFile(file) {
    const png = file.type === 'image/png' || (!file.type && /\.png$/i.test(file.name));
    if (!png) return replaceError('notPng');
    if (file.size > REPLACE_MAX_BYTES) return replaceError('tooHeavy');
    return null;
}

// A refused Replace, worded by its status (useFetch's `status`). A 400 is
// either not a PNG or too many pixels; the server names the size in the latter.
export function replaceErrorMessage(response) {
    if (response.status === 400) {
        const size = /(\d+)\s*x\s*(\d+)/.exec(response.error || '');
        return size ? replaceError('tooLarge', { width: Number(size[1]), height: Number(size[2]) }) : replaceError('notPng');
    }
    if (response.status === 413) return replaceError('tooHeavy');
    if (response.status === 409) return replaceError('busy');
    return replaceError('failed', { detail: response.error });
}

// A refused Mirror, worded by its status. A 409 carrying `attempts` means a
// re-edit landed since the page loaded; any other 409 is a shot not under
// review or an edit still running.
export function mirrorErrorMessage(response) {
    const key = name => ({ key: `photoReview.mirror.errors.${name}`, values: {} });
    if (response.status === 409) return key(Number.isInteger(response.attempts) ? 'changed' : 'busy');
    return key('failed');
}

// A refused Unreject, worded by its status. A 409 carrying `attempts` means
// the shot changed since the page loaded; any other 409 is a shot no longer
// rejected, most likely reshot meanwhile.
export function unrejectErrorMessage(response) {
    const key = name => ({ key: `photoReview.unreject.errors.${name}`, values: {} });
    if (response.status === 409) return key(Number.isInteger(response.attempts) ? 'changed' : 'notRejected');
    return key('failed');
}

export function processNowMessage(result) {
    const stuck = result.stuck || 0;
    if (result.skipped === 'job-in-flight') {
        return { key: 'photoReview.processNow.inFlight', values: {}, severity: 'info' };
    }
    if (result.skipped === 'queue-empty' || !result.submitted) {
        return stuck
            ? { key: 'photoReview.processNow.queueEmptyStuck', values: { stuck }, severity: 'info' }
            : { key: 'photoReview.processNow.queueEmpty', values: {}, severity: 'info' };
    }
    return stuck
        ? { key: 'photoReview.processNow.submittedStuck', values: { count: result.submitted, stuck }, severity: 'success' }
        : { key: 'photoReview.processNow.submitted', values: { count: result.submitted }, severity: 'success' };
}
